import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import startedImg from '../../images/search-started.webp';
import arrowImg from '../../images/search-wait-arrow.webp';
import css from './SearchStartedNotice.module.scss';

interface SearchStartedNoticeProps {
  count: number;
  onAcknowledge: () => void;
}

const SearchStartedNotice: React.FC<SearchStartedNoticeProps> = ({ count, onAcknowledge }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const waitTimeRef = useRef<HTMLElement>(null);
  const [arrowPosition, setArrowPosition] = useState<React.CSSProperties | null>(null);
  const plural = count > 1;
  const positionArrow = useCallback(() => {
    if (!frameRef.current || !waitTimeRef.current) return;
    const frame = frameRef.current.getBoundingClientRect();
    const target = waitTimeRef.current.getBoundingClientRect();
    if (!frame.width || !target.width) return;
    // Let the tail escape onto the mask, keeping the image within the viewport
    // on phones. The arrow's tip is at the left, 79% down the asset.
    const outside = Math.min(112, Math.max(0, window.innerWidth - frame.right - 2));
    const left = target.right - frame.left + 8;
    const width = frame.width + outside - left;
    const heightLimit = parseFloat(getComputedStyle(frameRef.current).getPropertyValue('--arrow-height-limit')) || 110;
    const height = Math.min(heightLimit, width * 377 / 600);
    setArrowPosition({ left, top: target.top - frame.top + target.height / 2 - height * 0.79,
      width, height });
  }, []);

  useEffect(() => {
    if (count === 0) {
      setArrowPosition(null);
      return;
    }
    const observer = new ResizeObserver(positionArrow);
    if (frameRef.current) observer.observe(frameRef.current);
    if (waitTimeRef.current) observer.observe(waitTimeRef.current);
    window.addEventListener('resize', positionArrow);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', positionArrow);
    };
  }, [count, positionArrow]);
  return (
    <Modal
      title={plural ? 'Your searches have started' : 'Your search has started'}
      open={count > 0}
      centered
      width="min(600px, 94vw)"
      closable={false}
      maskClosable={false}
      keyboard={false}
      className={css.noticeModal}
      afterOpenChange={(open) => {
        if (open) {
          positionArrow();
          buttonRef.current?.focus();
        }
      }}
      modalRender={(modal) => (
        <div ref={frameRef} className={css.noticeFrame}>
          {modal}
          {count > 0 && arrowPosition && (
            <img src={arrowImg} alt="" aria-hidden="true" className={css.pointingArrow}
              style={arrowPosition} />
          )}
        </div>
      )}
      footer={[
        <Button key="acknowledge" ref={buttonRef} type="primary" onClick={onAcknowledge}>
          Got it
        </Button>,
      ]}
    >
      <img
        src={startedImg}
        alt="Beast carefully examining job ads and company profiles with a magnifier, beside a neat pile and an hourglass"
        className={css.guideImage}
      />
      <div className={css.copy}>
        <p>
          Finding the right roles for you takes{' '}
          <strong ref={waitTimeRef} className={css.waitTime}>up to a few hours</strong>.
        </p>
        <p>
          We're not just pulling a list of job ads. We research each company, read every ad,
          filter out the ones that don't fit you, and find the right people to reach.
          That deep, tailor-made work takes time.
        </p>
        <p>
          <strong>
            <span className={css.emailEmphasis}>We'll email you</span>{' '}
            as soon as your first matches are ready.
          </strong>{' '}
          In the meantime, you can follow {plural ? 'their' : 'its'} progress on the search card below.
        </p>
      </div>
    </Modal>
  );
};

export default SearchStartedNotice;
